import React, { useEffect, useMemo, useState } from 'react';
import { Reservation } from '../../interfaces';

interface WaitDetails {
    partySize: number;
    quotedMinutes: number;
    guest: Reservation['guest'];
    guestNotes?: string;
}

interface Props {
    /**
     * An entry already in the queue, to correct. The same four things are worth
     * changing as were worth asking for, which is why this doubles as the edit
     * form rather than sending a queue entry to the booking sheet — that asks for
     * a date, a time and a table, none of which a party at the door has.
     */
    entry?: Reservation | null;
    /**
     * What the floor suggests quoting for this party size: the soonest a big
     * enough table should come free, in minutes. Undefined when nothing on the
     * floor fits them, which is itself worth saying out loud.
     */
    suggestQuote: (partySize: number) => number | undefined;
    onJoin: (entry: WaitDetails) => Promise<Reservation | null>;
    onSave: (entry: Reservation, changes: WaitDetails) => Promise<Reservation | null>;
    onClose: () => void;
}

const PARTY_MIN = 1;
const PARTY_MAX = 30;
const QUOTE_STEP = 5;
const QUOTE_MAX = 180;

const icons = {
    close: <svg viewBox='0 0 14 14' width='12' height='12' fill='none'><path d='M13 1L1 13M1 1l12 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' /></svg>,
    minus: <svg viewBox='0 0 24 24' fill='none'><path d='M5 12h14' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' /></svg>,
    plus: <svg viewBox='0 0 24 24' fill='none'><path d='M12 5v14M5 12h14' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' /></svg>,
};

const splitName = (full: string): { firstName: string; lastName?: string } => {
    const parts = full.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') || undefined };
};

const WaitlistSheet: React.FC<Props> = ({ entry, suggestQuote, onJoin, onSave, onClose }) => {
    const editing = !!entry;
    const [name, setName] = useState(entry
        ? `${entry.guest?.firstName || 'Guest'}${entry.guest?.lastName ? ` ${entry.guest.lastName}` : ''}`
        : '');
    const [phone, setPhone] = useState(entry?.guest?.phone || '');
    const [partySize, setPartySize] = useState(entry?.partySize ?? 2);
    const [notes, setNotes] = useState(entry?.guestNotes || '');
    const [quote, setQuote] = useState(entry?.waitlist?.quotedMinutes ?? 20);
    /**
     * Once the host sets a quote by hand, stop moving it under them — and a quote
     * already given to a waiting party counts as set by hand.
     */
    const [quoteTouched, setQuoteTouched] = useState(editing);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const suggested = useMemo(() => suggestQuote(partySize), [suggestQuote, partySize]);

    useEffect(() => {
        if (quoteTouched) return;
        setQuote(suggested === undefined ? 30 : Math.max(QUOTE_STEP, Math.ceil(suggested / QUOTE_STEP) * QUOTE_STEP));
    }, [suggested, quoteTouched]);

    const handleSubmit = async () => {
        const { firstName, lastName } = splitName(name);
        if (!firstName) {
            setError('A name is needed to call the party.');
            return;
        }
        const details: WaitDetails = {
            partySize,
            quotedMinutes: quote,
            guest: { firstName, lastName, phone: phone.trim() || undefined },
            guestNotes: notes.trim() || undefined,
        };
        setSaving(true);
        setError('');
        try {
            const saved = entry ? await onSave(entry, details) : await onJoin(details);
            if (saved) onClose();
            else setError('That did not save. Try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className='resv-scrim' onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div
                className='resv-sheet'
                onMouseDown={(e) => e.stopPropagation()}
                role='dialog'
                aria-label={editing ? 'Edit the wait' : 'Add to the queue'}
            >
                <div className='resv-sheet__head'>
                    <div className='resv-sheet__headtext'>
                        <div className='resv-sheet__title'>{editing ? 'Edit the wait' : 'Add to the queue'}</div>
                        <div className='resv-sheet__sub'>
                            {editing
                                ? `In the queue since ${entry!.time}.`
                                : 'A walk-in with no table free yet.'}
                        </div>
                    </div>
                    <button className='resv-x' onClick={onClose} aria-label='Close'>{icons.close}</button>
                </div>

                <div className='resv-sheet__body'>
                    <section className='resv-sec'>
                        <div className='resv-field'>
                            <label className='resv-label' htmlFor='wait-name'>Name</label>
                            <input
                                id='wait-name'
                                className='resv-input'
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder='Whoever you will call'
                                autoFocus
                            />
                        </div>

                        <div className='resv-field'>
                            <label className='resv-label' htmlFor='wait-phone'>Phone <span className='resv-label__opt'>optional</span></label>
                            <input
                                id='wait-phone'
                                className='resv-input'
                                type='tel'
                                inputMode='tel'
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder='So they can wait at the bar'
                            />
                        </div>

                        <div className='resv-field'>
                            <label className='resv-label'>Party size</label>
                            <div className='resv-stepper'>
                                <button
                                    type='button'
                                    className='resv-stepper__btn'
                                    onClick={() => setPartySize(p => Math.max(PARTY_MIN, p - 1))}
                                    disabled={partySize <= PARTY_MIN}
                                    aria-label='Fewer guests'
                                >{icons.minus}</button>
                                <div className='resv-stepper__val'>
                                    {partySize}
                                    <span>{partySize === 1 ? 'guest' : 'guests'}</span>
                                </div>
                                <button
                                    type='button'
                                    className='resv-stepper__btn'
                                    onClick={() => setPartySize(p => Math.min(PARTY_MAX, p + 1))}
                                    disabled={partySize >= PARTY_MAX}
                                    aria-label='More guests'
                                >{icons.plus}</button>
                            </div>
                        </div>

                        <div className='resv-field'>
                            <label className='resv-label'>Quote them</label>
                            <div className='resv-stepper'>
                                <button
                                    type='button'
                                    className='resv-stepper__btn'
                                    onClick={() => { setQuoteTouched(true); setQuote(q => Math.max(QUOTE_STEP, q - QUOTE_STEP)); }}
                                    disabled={quote <= QUOTE_STEP}
                                    aria-label='Shorter wait'
                                >{icons.minus}</button>
                                <div className='resv-stepper__val'>
                                    {quote}
                                    <span>minutes</span>
                                </div>
                                <button
                                    type='button'
                                    className='resv-stepper__btn'
                                    onClick={() => { setQuoteTouched(true); setQuote(q => Math.min(QUOTE_MAX, q + QUOTE_STEP)); }}
                                    disabled={quote >= QUOTE_MAX}
                                    aria-label='Longer wait'
                                >{icons.plus}</button>
                            </div>
                            {/* A div, not a p: the design system forces every p to inherit its
                              * parent's size, which would blow this hint up to body text. */}
                            <div className='resv-hint'>
                                {editing && quote !== entry!.waitlist?.quotedMinutes
                                    ? 'A changed quote counts from now, the same as a re-quote.'
                                    : suggested === undefined
                                        ? `Nothing on this floor seats ${partySize}, so this is a guess.`
                                        : suggested === 0
                                            ? 'A table that size is free now — you may not need the queue at all.'
                                            : `The floor suggests about ${suggested} minutes: that is when a table this size should turn.`}
                            </div>
                        </div>

                        <div className='resv-field'>
                            <label className='resv-label' htmlFor='wait-notes'>Note <span className='resv-label__opt'>optional</span></label>
                            <textarea
                                id='wait-notes'
                                className='resv-input resv-input--area'
                                rows={2}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder='Terrace is fine, waiting at the bar…'
                            />
                        </div>
                    </section>
                </div>

                <div className='resv-sheet__foot'>
                    {error && <span className='resv-error'>{error}</span>}
                    <div className='resv-sheet__footbtns'>
                        <button type='button' className='resv-btn resv-btn--ghost' onClick={onClose}>Cancel</button>
                        <button type='button' className='resv-btn resv-btn--primary' onClick={handleSubmit} disabled={saving}>
                            {editing
                                ? (saving ? 'Saving…' : 'Save')
                                : (saving ? 'Adding…' : `Add and quote ${quote}m`)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WaitlistSheet;
